import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LeaderComponent } from '../leader.component';
import {
    UserService,
    CampusWard,
    WardDirectoryParameters,
    CampusWardsDataService,
    AcademicSession,
    HousingDataService,
    SortService,
    MemberInfo,
    WardGroupType,
    WardGroup,
    WardGroupAssignments,
    ApprovedAddress,
    WardGroupAssignment,
    NavigationService
} from '../../shared';
import { MatDialog } from '@angular/material/dialog';
import { DirectorySortOrder } from '../../shared/models/ecclesiastical/directory-sort-order.enum';
import { FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

@Component({
    templateUrl: './ward-directory.component.html',
    styleUrls: ['./ward-directory.component.scss'],
    encapsulation: ViewEncapsulation.None // for kendo-pdf-export styling
})
export class WardDirectoryComponent extends LeaderComponent implements OnInit {
    groupedMembers = new Map<string, MemberInfo[]>();
    groupedAssignedApprovedAddress = new Map<string, ApprovedAddress>();
    /**
     * Whether or not our directory is the "administrative" or for leaders; if so we let them include more data.
     */
    isWardAdministration = true;
    ALL = 'All';
    FEMALE = 'Female';
    MALE = 'Male';

    /**
     * For our radio button options.
     */
    addressOrderBy = DirectorySortOrder.Address;
    lastNameOrderBy = DirectorySortOrder.LastName;
    filteredStudents: MemberInfo[];
    allStudents: MemberInfo[];

    showDirectory = false;
    allowDirectoryLoad = false;
    creatingPDF = false;
    loading = false;
    loadingError = false;
    isSinglesWard = false;
    ward: CampusWard;
    hasGroupTypes = false;
    noContent = false;

    academicSessions: AcademicSession[];
    selectedAcademicSession: AcademicSession;
    directoryParameters: WardDirectoryParameters;
    groups: WardGroup[];
    wardGroupAssignments: WardGroupAssignment[] = [];
    groupAssingments: WardGroupAssignments[] = [];
    navigationService: NavigationService;

    groupTypes: WardGroupType[];
    selectedGroupType: WardGroupType;
    showingGroup: string;

    filterForm: FormGroup;
    genderFilter = new FormControl();
    wardGroupFilter = new FormControl();
    wardSubGroupFilter = new FormControl();

    // Kendo pdf: does not require a license. Documentation is found in the link:
    // https://libraries.io/npm/@progress%2Fkendo-angular-pdf-export
    nosplit = '.keep-together-at-print'; // for kendo pdf print

    constructor(
        protected userService: UserService,
        protected dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute,
        private campusWardsService: CampusWardsDataService,
        private housingService: HousingDataService,
        private sorter: SortService,
        private formBuilder: FormBuilder,
        private _navigationService: NavigationService
    ) {
        super(userService, dialog);
        this.navigationService = _navigationService;
        this.directoryParameters = new WardDirectoryParameters();
    }

    ngOnInit() {
        super.ngOnInit();
        this.ward = this.getWard();
        this.directoryParameters.sortBy = DirectorySortOrder.LastName;

        // look which route they came in from and set whether or not we'll show them more options for generating an
        // administrative directory versus a ward member directory
        // this.isWardAdministration = this.route.snapshot.data.admin;
        if (this.route.snapshot.data['admin']) {
            this.isWardAdministration = this.route.snapshot.data['admin'];
        } else {
            this.isWardAdministration = false;

            this.directoryParameters.includeTrack = false;
            this.directoryParameters.includeBirthdate = false;
            this.directoryParameters.includeHomeUnit = false;
            this.directoryParameters.includeMission = false;
        }

        this.filterForm = this.formBuilder.group({
            genderFilter: this.genderFilter,
            wardGroupFilter: this.wardGroupFilter,
            wardSubGroupFilter: this.wardSubGroupFilter
        });

        this.genderFilter.valueChanges.subscribe(newValue =>
            this.applyGenderFilter(newValue, true)
        );
        this.wardGroupFilter.valueChanges.subscribe(newValue => {
            if (newValue) {
                this.applyWardGroupFilter(newValue, true);
            }
        });
        this.wardSubGroupFilter.valueChanges.subscribe(newValue => {
            if (newValue) {
                this.applyWardSubGroupFilter(newValue);
            }
        });

        if (this.ward && !this.ward.isForMarriedStudents) {
            this.isSinglesWard = true;
        }
        if (this.ward != null) {
            // this.loadData();
            this.initializeWardDirectory();
        }
    }


    initializeWardDirectory() {
        this.resetCurrentDirectoryData();
        this.noContent = false;
        this.loadCurrentWardDirectory();
        this.loadAcademicSessions();
    }

    resetCurrentDirectoryData() {
        this.filteredStudents = [];
        this.allStudents = [];
        this.allowDirectoryLoad = false;
        this.showDirectory = false;
        this.loadingError = false;
        this.loading = false;
    }

    /**
     * Update the ward that we're working with when the stake leader or admin selects a different ward.
     */
    onWardSelected(ward: CampusWard) {
        this.ward = ward;
        this.resetCurrentDirectoryData();

        if (this.ward != null) {
            this.loadData();
        }
    }

    onAcademicSessionSelected() {
        this.noContent = false;
    }

    /**
     * Loads the current ward directory (for the current session)
     */
    loadCurrentWardDirectory() {
        this.noContent = false;

        if (this.ward != null) {
            this.creatingPDF = true;
            this.campusWardsService
                .getCurrentCampusWardDirectory(
                    this.ward.campusWardId
                )
                .subscribe(
                    results => {
                        if (!results || results.length === 0) {
                            this.noContent = true;
                        }
                        this.filteredStudents = results;
                        this.allStudents = results;
                        this.groupStudentsByApartmentAddress();
                        if (
                            this.filteredStudents.length > 0 &&
                            this.filteredStudents != null
                        ) {
                            this.showDirectory = true;
                            this.creatingPDF = false;
                            this.allowDirectoryLoad = true;
                        } else {
                            this.creatingPDF = false;
                            this.allowDirectoryLoad = true;
                        }
                    },
                    error => {
                        this.loadingError = true;
                    }
                );
        }
    }

    loadAcademicSessions() {
        this.housingService
            .getAcademicSessions()
            .pipe(
                finalize(() => (this.loading = false))
            )
            .subscribe(
                semesters => {
                    this.loadCampusWardGroups(this.ward);
                    this.hasGroupTypes = true;
                    this.setAcademicSessions(semesters);
                    this.getAssignedApprovedAddress();
                },
                error => {
                    this.loadingError = true;
                }
            );
    }

    /**
     * Set the first two academic sessions
     * @param academicSessions academicSessions
     */
    setAcademicSessions(academicSessions: AcademicSession[]) {
        var _academicSessions = academicSessions;
        this.academicSessions = [];
        academicSessions.sort(this.sorter.sortAcademicSessionsAsc);
        if (_academicSessions.length > 0) {
            this.selectedAcademicSession = _academicSessions[0];
            this.academicSessions.push(_academicSessions[0])
        }
        if (_academicSessions.length > 1) {
            this.academicSessions.push(_academicSessions[1]);
        }
    }

    getWardDirectory() {
        this.resetCurrentDirectoryData();
        this.noContent = false;
        const excludeNonAttendingLocalStudents = true;

        if (this.ward != null) {
            this.creatingPDF = true;
            this.getAssignedApprovedAddress();
            this.campusWardsService
                .getCampusWardDirectory(this.ward.campusWardId, this.selectedAcademicSession.code, this.selectedAcademicSession.year, excludeNonAttendingLocalStudents)
                .subscribe(results => {
                    if (!results || results.length === 0) {
                        this.noContent = true;
                    }
                    this.filteredStudents = results;
                    this.allStudents = results;
                    this.groupStudentsByApartmentAddress();
                    if (this.filteredStudents.length > 0 && this.filteredStudents != null) {
                        this.showDirectory = true;
                        this.creatingPDF = false;
                        this.allowDirectoryLoad = true;
                    } else {
                        this.creatingPDF = false;
                        this.allowDirectoryLoad = true;
                    }
                },
                    error => {
                        this.loadingError = true;
                    }
                );
        }
    }

    /**
     * Get academic sessions
     * @param academicSessions
     */
    private handleSessions(academicSessions: AcademicSession[]) {
        this.academicSessions = academicSessions;
        this.academicSessions.sort(this.sorter.sortAcademicSessionsAsc);
        if (academicSessions.length > 0) {
            this.selectedAcademicSession = this.academicSessions[0];
            this.getWardDirectory();
            this.getAssignedApprovedAddress();
        }
    }

    getAssignedApprovedAddress() {
        if (this.ward !== null) {
            this.housingService
                .getAssignedApprovedWardAddresses(
                    this.ward.campusWardId,
                    this.selectedAcademicSession.sessionNumber
                )
                .subscribe(
                    results => {
                        this.setAssignedApprovedAddresses(results);
                    },
                    error => {
                        this.loadingError = true;
                    }
                );
        }
    }

    getGroupAssignments(campusWardId: number, group: WardGroup) {
        this.campusWardsService
            .getWardGroupAssignments(
                campusWardId,
                group.type.campusWardGroupTypeId,
                this.selectedAcademicSession.year,
                this.selectedAcademicSession.code
            )
            .subscribe(results => {
                this.groupAssingments.push(results);
                this.wardGroupAssignments.push.apply(
                    this.wardGroupAssignments,
                    results.assignments
                );
            });
    }

    /**
     * Get group types and academic sessions
     */
    private loadData() {
        this.noContent = false;
        this.loading = true;
        this.housingService
            .getAcademicSessions()
            .pipe(
                finalize(() => (this.loading = false))
            )
            .subscribe(
                semesters => {
                    this.loadCampusWardGroups(this.ward);
                    this.hasGroupTypes = true;
                    this.handleSessions(semesters);
                },
                error => {
                    this.loadingError = true;
                }
            );
    }

    loadCampusWardGroups(campusWard: CampusWard) {
        forkJoin(
            this.campusWardsService.getWardGroupTypes(),
            this.campusWardsService.getWardGroups(campusWard.campusWardId)
        ).subscribe(([groupTypes, groups]) => {
            this.groupTypes = groupTypes;
            this.groups = groups;
            this.groups.sort(this.sorter.sortWardGroupsAsc);
            for (const group of this.groups) {
                this.getGroupAssignments(this.ward.campusWardId, group);
            }
        });
    }

    sortDirectoryByLastName() {
        this.filteredStudents.sort(this.sorter.sortWardDirectoryByLastName);
    }

    sortDirectoryByAddress() {
        this.filteredStudents.sort(this.sorter.sortWardDirectoryByAddress);
    }

    /**
     * Filters students by the gender specified in the parameter
     * @param gender gender
     */
    applyGenderFilter(gender: string, resetAllFilters?: boolean) {
        if (!this.filteredStudents) {
            return;
        }

        if (gender === this.MALE || gender === 'Male') {
            this.filteredStudents = this.allStudents.filter(x => x.sex === 'Male');
            this.groupStudentsByApartmentAddress();
        } else if (gender === this.FEMALE || gender === 'Female') {
            this.filteredStudents = this.allStudents.filter(x => x.sex === 'Female');
            this.groupStudentsByApartmentAddress();
        } else {
            this.filteredStudents = this.allStudents;
            this.groupStudentsByApartmentAddress();
        }

        if (resetAllFilters) {
            this.wardSubGroupFilter.setValue(this.ALL, {
                onlySelf: true,
                emitEvent: false
            });
            this.wardGroupFilter.setValue(this.ALL, {
                onlySelf: true,
                emitEvent: false
            });
        }
    }

    /**
     * Filters students that are assigned to the ward group specified in the parameter.
     * @param groupTypeName the ward group type to filter students by. E.g 'Relief Society' or 'Elders Quorum'
     */
    applyWardGroupFilter(groupType, resetSubGroup?: boolean) {
        if (!this.allStudents || !groupType) {
            return;
        }

        // Apply the gender filter first
        if (groupType === this.ALL) {
            // apply the current gender filter if it was set. Set to All otherwise
            const gender = this.genderFilter.value
                ? this.genderFilter.value
                : this.ALL;

            this.genderFilter.setValue(gender, { onlySelf: true, emitEvent: true });
            return;
        } else if (!groupType.allowMales) {
            this.genderFilter.setValue(this.FEMALE, {
                onlySelf: true,
                emitEvent: false
            });
            this.applyGenderFilter(this.FEMALE, false);
        } else if (!groupType.allowFemales) {
            this.genderFilter.setValue(this.MALE, {
                onlySelf: true,
                emitEvent: false
            });
            this.applyGenderFilter(this.MALE, false);
        } else if (groupType.allowFemales && groupType.allowMales) {
            this.genderFilter.setValue(this.ALL, {
                onlySelf: true,
                emitEvent: false
            });
            this.applyGenderFilter(this.ALL, false);
        }

        // reset the subgroup filter
        if (resetSubGroup) {
            this.wardSubGroupFilter.setValue(this.ALL, {
                onlySelf: true,
                emitEvent: false
            });
        }

        const groupName = groupType.name ? groupType.name : this.ALL;

        // finally apply the group filter
        this.filteredStudents = this.getStudentsInWardGroup(groupName);
        this.groupStudentsByApartmentAddress();
    }

    /**
     * Filters students that are assigned to the ward sub group specified in the
     * parameter.
     * @param wardGroup the ward sub group e.g RS 1, EQ3, FHE 1
     */
    applyWardSubGroupFilter(wardGroup) {
        if (!wardGroup || !this.allStudents || !this.allStudents) {
            return;
        }

        const subGroupName = wardGroup.name ? wardGroup.name : this.ALL;

        const groupType = wardGroup.type ? wardGroup.type : this.ALL;

        if (groupType === this.ALL) {
            // maintain the current ward group filter
            const currentGroupType = this.wardGroupFilter.value;
            const index = this.groupTypes.findIndex(
                gt => gt.name === currentGroupType.name
            );

            if (index >= 0) {
                this.wardGroupFilter.patchValue(this.groupTypes[index], {
                    onlySelf: true,
                    emitEvent: false
                });
                this.applyWardGroupFilter(this.groupTypes[index], false);
            }
        } else {
            // Select the appropriate group type and update the group type filter
            const groupTypeName = groupType.name ? groupType.name : this.ALL;
            const groupTypeIndex = this.groupTypes.findIndex(
                g => g.name === groupTypeName
            );

            if (groupTypeIndex >= 0) {
                this.wardGroupFilter.patchValue(this.groupTypes[groupTypeIndex], {
                    onlySelf: true,
                    emitEvent: false
                });
                this.applyWardGroupFilter(this.groupTypes[groupTypeIndex], false);
            }
        }

        // finally apply the sub group filter
        if (subGroupName !== this.ALL) {
            this.filteredStudents = this.getStudentsInWardSubGroup(subGroupName);
            this.groupStudentsByApartmentAddress();
        }
    }

    /**
     * Returns an array of all members in the primary ward group specified in the parameter
     * @param groupTypeName group type name. E.g 'Relief Society', 'Elders Quorum' 'FHE'
     */
    getStudentsInWardGroup(groupTypeName: string): MemberInfo[] {
        if (!this.allStudents || !groupTypeName) {
            return;
        }

        const groupTypeindex = this.groupTypes.findIndex(
            t => t.name === groupTypeName
        );

        if (groupTypeindex < 0) {
            this.filteredStudents = [];
            return;
        }

        const groupTypeId = this.groupTypes[groupTypeindex].campusWardGroupTypeId;
        const subGroups = this.groups.filter(
            g => g.type.campusWardGroupTypeId === groupTypeId
        );

        const result: MemberInfo[] = [];

        subGroups.forEach(sub => {
            const groupMembers = this.getStudentsInWardSubGroup(sub.name);
            result.push.apply(result, groupMembers);
        });

        return result;
    }

    /**
     * Returns an array of all members in the ward sub group specified in the parameter
     * @param wardSubGroupName ward sub group name e.g 'RS 1', 'EQ 2', 'FHE 5'
     */
    getStudentsInWardSubGroup(wardSubGroupName: string): MemberInfo[] {
        let result: MemberInfo[] = [];

        if (!wardSubGroupName || !this.allStudents) {
            return result;
        }

        const index = this.groups.findIndex(g => g.name === wardSubGroupName);

        if (index < 0) {
            this.filteredStudents = [];
            return;
        }

        // get the student addresses assigned to the selected group
        const groupId = this.groups[index].campusWardGroupId;
        const groupAddresses = this.wardGroupAssignments
            .filter(g => g.campusWardGroupId === groupId)
            .map(g => g.approvedAddress);

        // TODO change to one line after testing
        result = this.getAllStudentsWithAddresses(groupAddresses);
        return result;
    }

    /**
     * Returns an array of students that have addresses tha match any address in
     * the passed parameter
     * @param addresses Array of addresses to find students in
     */
    getAllStudentsWithAddresses(addresses: string[]): MemberInfo[] {
        const result: MemberInfo[] = [];

        if (!addresses) {
            return result;
        }

        // Using a map for easy and faster lookup. O(n) vs O(n^2) if using the array
        const addressMap = new Map<string, string>();
        addresses.forEach(a => {
            addressMap.set(a, a);
        });

        for (const student of this.allStudents) {
            if (addressMap.has(student.address)) {
                result.push(student);
            }
        }

        return result;
    }

    /**
     * Returns all the students in a single appartment.
     * Returns an empty array if no students exist in the
     * apartment.
     * @param address address
     */
    getStudentsInApartment(address: string): MemberInfo[] {
        if (this.groupedMembers.has(address)) {
            const students = this.groupedMembers
                .get(address)
                .sort(this.sorter.sortWardDirectoryByLastName);

            return students;
        } else {
            return [];
        }
    }

    /**
     * Returns an array of filtered students sorted by last name
     */
    getSortedFilterredMembers(): MemberInfo[] {
        return this.filteredStudents.sort(this.sorter.sortWardDirectoryByLastName);
    }

    /**
     * Groups students into a map using the appartment address as the key
     */
    private groupStudentsByApartmentAddress() {
        if (!this.filteredStudents) {
            this.groupedMembers.clear();
            return;
        }

        if (this.groupedMembers) {
            this.groupedMembers.clear();
        }

        for (const member of this.filteredStudents) {
            if (this.groupedMembers.has(member.address)) {
                const addressGroup = this.groupedMembers.get(member.address);
                addressGroup.push(member);

                this.groupedMembers.set(member.address, addressGroup);
            } else {
                const newAddressGroup = [member];
                this.groupedMembers.set(member.address, newAddressGroup);
            }
        }
    }

    /**
     * Adds the array of approved address into a local map for easy
     * grouping and referencing.
     * @param approvedAddresses approved addresses assigned to the ward
     */
    private setAssignedApprovedAddresses(approvedAddresses: ApprovedAddress[]) {
        if (approvedAddresses === null) {
            return;
        }

        for (const address of approvedAddresses) {
            if (!this.groupedAssignedApprovedAddress.has(address.fullAddress)) {
                this.groupedAssignedApprovedAddress.set(address.fullAddress, address);
            }
        }
    }

    /**
     * Returns a sorted list of unique student addresses in the ward
     */
    getGroupedAddresses(): string[] {
        return Array.from(this.groupedMembers.keys()).sort();
    }

    /**
     * Returns the number of beds in an apartment
     * @param address appartment address
     */
    getAddressCapacity(address: string): string {
        if (this.groupedAssignedApprovedAddress.has(address)) {
            const result = this.groupedAssignedApprovedAddress.get(address).totalResidents;
            return result.toString();
        }
        return 'N/A';
    }

    /**
     * Returns the total number of students in the appartment
     * @param address appartment address
     */
    getAddressActualOccupants(address: string): string {
        if (this.groupedAssignedApprovedAddress.has(address)) {
            const result = this.groupedAssignedApprovedAddress.get(address).actualResidents;
            return result.toString();
        }
        return 'N/A';
    }

    getExportFilename(extension) {
        let filename = this.ward ? this.ward.name : 'Ward Directory';
        filename += (this.selectedAcademicSession && this.selectedAcademicSession.description)
            ? ' - ' + this.selectedAcademicSession.description
            : '';
        filename += (this.wardGroupFilter && this.wardGroupFilter.value && this.wardGroupFilter.value.name)
            ? ' - ' + this.wardGroupFilter.value.name
            : '';
        filename += (this.wardSubGroupFilter && this.wardSubGroupFilter.value && this.wardSubGroupFilter.value.name)
            ? ' - ' + this.wardSubGroupFilter.value.name
            : '';

        if (extension) {
            filename = `${filename}.${extension}`;
        }

        return filename;
    }

    filterredByGender(): boolean {
        return this.genderFilter.value !== this.ALL;
    }

    filterredByWardGroup(): boolean {
        return (
            this.wardGroupFilter.value && this.wardGroupFilter.value !== this.ALL
        );
    }

    filterredByWardSubGroup(): boolean {
        return (
            this.wardSubGroupFilter.value &&
            this.wardSubGroupFilter.value !== this.ALL
        );
    }

}
